/**
 * ⚠️ LR DIRECT ONLY — single source of truth for "is this load still live?"
 *
 * A direct load is only biddable when BOTH are true:
 *   1. its status is still an open status (`posted` / `bidding`), and
 *   2. its bid deadline has not passed (a null deadline means no cutoff).
 *
 * Never infer "live" from status alone — the expiry job runs on a schedule,
 * so a row can legitimately sit in `posted` for a few minutes after its
 * deadline. Everything user-facing must compute from status + deadline.
 */

export interface DirectLoadStatusFields {
  status?: string | null;
  bid_deadline?: string | null;
}

const OPEN_STATUSES = new Set(["posted", "bidding"]);
const TERMINAL_STATUSES = new Set(["expired", "canceled", "cancelled", "completed"]);

/** True when the bid deadline has already passed. Null deadline = no cutoff. */
export function isDeadlinePassed(
  bidDeadline: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!bidDeadline) return false;
  const t = new Date(bidDeadline).getTime();
  if (Number.isNaN(t)) return false;
  return t <= now;
}

/** True only when brokers can still place a bid on this load right now. */
export function isBiddingOpen(
  load: DirectLoadStatusFields | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!load) return false;
  // A missing/undefined status means the caller didn't select the column (the
  // row was already status-filtered server side) — judge on the deadline only.
  // An explicitly closed status still fails. Never silently drop every row.
  const status = load.status;
  if (status != null && status !== "" && !OPEN_STATUSES.has(String(status))) return false;
  return !isDeadlinePassed(load.bid_deadline, now);
}

/**
 * True when the load is closed to bidding: terminal status, or an open status
 * whose deadline has passed (stale row awaiting the expiry sweep).
 */
export function isClosed(
  load: DirectLoadStatusFields | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!load) return false;
  const status = String(load.status ?? "");
  if (TERMINAL_STATUSES.has(status)) return true;
  if (OPEN_STATUSES.has(status)) return isDeadlinePassed(load.bid_deadline, now);
  return false;
}

/**
 * PostgREST `.or()` clause that keeps only loads whose deadline is still in
 * the future (or that have no deadline at all). Pair with `.eq("status", ...)`.
 */
export function biddingOpenOrFilter(now: Date = new Date()): string {
  return `bid_deadline.is.null,bid_deadline.gt.${now.toISOString()}`;
}
